'use client';

import * as React from 'react';
import {
  type CalendarDefaultDuration,
  type CalendarDefaultView,
} from '@/lib/settings/appSettings';
import { useAppSettings } from '@/lib/settings/useAppSettings';
import {
  deviceLabel,
  listMediaDevices,
  supportsAudioOutputSelection,
} from '@/lib/settings/mediaDevices';
import styles from './SettingsPage.module.css';

function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <label className={styles.toggleRow}>
      <input
        type="checkbox"
        className={styles.toggleInput}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className={styles.toggleText}>
        <span className={styles.toggleLabel}>{label}</span>
        {description ? <span className={styles.toggleHint}>{description}</span> : null}
      </span>
    </label>
  );
}

function DevicePreview({
  videoDeviceId,
  audioDeviceId,
}: {
  videoDeviceId: string;
  audioDeviceId: string;
}) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let stream: MediaStream | null = null;
    let cancelled = false;

    async function start() {
      setError(null);
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video:
            videoDeviceId && videoDeviceId !== 'default'
              ? { deviceId: { exact: videoDeviceId } }
              : true,
          audio:
            audioDeviceId && audioDeviceId !== 'default'
              ? { deviceId: { exact: audioDeviceId } }
              : true,
        });
        if (cancelled) return;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Не удалось получить доступ к камере');
        }
      }
    }

    void start();

    return () => {
      cancelled = true;
      stream?.getTracks().forEach((track) => track.stop());
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [videoDeviceId, audioDeviceId]);

  return (
    <div className={styles.previewWrap}>
      <video ref={videoRef} className={styles.previewVideo} muted playsInline autoPlay />
      {error ? <div className={styles.previewError}>{error}</div> : null}
    </div>
  );
}

export default function SettingsPage() {
  const { settings, update } = useAppSettings();
  const [devices, setDevices] = React.useState<Awaited<ReturnType<typeof listMediaDevices>>>({
    audioInputs: [],
    videoInputs: [],
    audioOutputs: [],
  });

  const refreshDevices = React.useCallback(async () => {
    setDevices(await listMediaDevices());
  }, []);

  React.useEffect(() => {
    void refreshDevices();
    navigator.mediaDevices?.addEventListener('devicechange', refreshDevices);
    return () => navigator.mediaDevices?.removeEventListener('devicechange', refreshDevices);
  }, [refreshDevices]);

  const outputSupported = supportsAudioOutputSelection();

  return (
    <main className={styles.main}>
      <div className={styles.header}>
        <h1 className={styles.title}>Настройки</h1>
      </div>

      <div className={styles.grid}>
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Оборудование</h2>
          <p className={styles.cardHint}>Управление устройствами и настройка громкости</p>

          <DevicePreview
            videoDeviceId={settings.videoDeviceId}
            audioDeviceId={settings.audioDeviceId}
          />

          <label className={styles.field}>
            <span>Камера</span>
            <select
              className={styles.select}
              value={settings.videoDeviceId}
              onChange={(e) => update({ videoDeviceId: e.target.value })}
            >
              <option value="default">По умолчанию</option>
              {devices.videoInputs.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {deviceLabel(device, 'Камера')}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span>Микрофон</span>
            <select
              className={styles.select}
              value={settings.audioDeviceId}
              onChange={(e) => update({ audioDeviceId: e.target.value })}
            >
              <option value="default">По умолчанию</option>
              {devices.audioInputs.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {deviceLabel(device, 'Микрофон')}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span>Динамики</span>
            <select
              className={styles.select}
              value={settings.audioOutputDeviceId}
              disabled={!outputSupported}
              onChange={(e) => update({ audioOutputDeviceId: e.target.value })}
            >
              <option value="default">По умолчанию</option>
              {devices.audioOutputs.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {deviceLabel(device, 'Динамики')}
                </option>
              ))}
            </select>
            {!outputSupported ? (
              <span className={styles.fieldHint}>Браузер не поддерживает выбор динамиков.</span>
            ) : null}
          </label>

          <label className={styles.field}>
            <span>Громкость</span>
            <div className={styles.rangeRow}>
              <input
                type="range"
                min={0}
                max={100}
                value={settings.volume}
                className={styles.range}
                style={{ '--range-fill': `${settings.volume}%` } as React.CSSProperties}
                onChange={(e) => update({ volume: Number(e.target.value) })}
              />
              <span className={styles.rangeValue}>{settings.volume}%</span>
            </div>
          </label>
        </section>

        <div className={styles.sideColumn}>
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Звонок</h2>
            <p className={styles.cardHint}>Состояние камеры и микрофона на встрече</p>

            <div className={styles.toggleGroup}>
              <Toggle
                label="Включить микрофон при входе на встречу"
                checked={settings.audioEnabled}
                onChange={(audioEnabled) => update({ audioEnabled })}
              />

              <Toggle
                label="Включить камеру при входе на встречу"
                checked={settings.videoEnabled}
                onChange={(videoEnabled) => update({ videoEnabled })}
              />
            </div>
          </section>

          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Календарь</h2>
            <p className={styles.cardHint}>Значения календаря при открытии</p>

            <label className={styles.field}>
              <span>Вид по умолчанию</span>
              <select
                className={styles.select}
                value={settings.calendarDefaultView}
                onChange={(e) =>
                  update({ calendarDefaultView: e.target.value as CalendarDefaultView })
                }
              >
                <option value="week">Неделя</option>
                <option value="month">Месяц</option>
              </select>
            </label>

            <label className={styles.field}>
              <span>Длительность встречи по умолчанию</span>
              <select
                className={styles.select}
                value={settings.calendarDefaultDurationMinutes}
                onChange={(e) =>
                  update({
                    calendarDefaultDurationMinutes: Number(e.target.value) as CalendarDefaultDuration,
                  })
                }
              >
                <option value={15}>15 минут</option>
                <option value={30}>30 минут</option>
                <option value={60}>60 минут</option>
              </select>
            </label>
          </section>
        </div>
      </div>
    </main>
  );
}
